import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Alert,
  Stack,
  TextField,
  Collapse,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { customerService } from '../services/customerService';
import {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
  CreateDownloadTokenRequest,
  UpdateDownloadTokenRequest,
  CustomerDownloadToken,
} from '../types/api';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import { format } from 'date-fns';
import { env } from '../config/env';

export const CustomersPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [expandedCustomer, setExpandedCustomer] = useState<number | null>(null);
  const [statsDays, setStatsDays] = useState(7);
  const [addCustomerDialogOpen, setAddCustomerDialogOpen] = useState(false);
  const [editCustomerDialog, setEditCustomerDialog] = useState<{
    open: boolean;
    customer: Customer | null;
  }>({ open: false, customer: null });
  const [deleteCustomerDialog, setDeleteCustomerDialog] = useState<{
    open: boolean;
    customer: Customer | null;
  }>({ open: false, customer: null });

  const [addTokenDialog, setAddTokenDialog] = useState<{
    open: boolean;
    customerId: number | null;
  }>({ open: false, customerId: null });
  const [editTokenDialog, setEditTokenDialog] = useState<{
    open: boolean;
    customerId: number | null;
    token: CustomerDownloadToken | null;
  }>({ open: false, customerId: null, token: null });
  const [deleteTokenDialog, setDeleteTokenDialog] = useState<{
    open: boolean;
    customerId: number | null;
    token: CustomerDownloadToken | null;
  }>({ open: false, customerId: null, token: null });

  const [copiedTokenGuid, setCopiedTokenGuid] = useState<string | null>(null);

  const { data: customers, isLoading } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getAll(true),
  });

  // Fetch customer details when expanded
  const { data: customerDetails } = useQuery({
    queryKey: ['customer', expandedCustomer],
    queryFn: () => expandedCustomer ? customerService.getById(expandedCustomer) : null,
    enabled: expandedCustomer !== null,
  });

  // Fetch download statistics
  const { data: downloadStats } = useQuery({
    queryKey: ['download-statistics-all', statsDays],
    queryFn: () => customerService.getAllDownloadStatistics(statsDays),
  });

  const createCustomerMutation = useMutation({
    mutationFn: (request: CreateCustomerRequest) => customerService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setAddCustomerDialogOpen(false);
    },
  });

  const updateCustomerMutation = useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateCustomerRequest }) =>
      customerService.update(id, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditCustomerDialog({ open: false, customer: null });
    },
  });

  const deleteCustomerMutation = useMutation({
    mutationFn: (id: number) => customerService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleteCustomerDialog({ open: false, customer: null });
    },
  });

  const createTokenMutation = useMutation({
    mutationFn: ({ customerId, request }: { customerId: number; request: CreateDownloadTokenRequest }) =>
      customerService.createToken(customerId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setAddTokenDialog({ open: false, customerId: null });
    },
  });

  const updateTokenMutation = useMutation({
    mutationFn: ({
      customerId,
      tokenId,
      request,
    }: {
      customerId: number;
      tokenId: number;
      request: UpdateDownloadTokenRequest;
    }) => customerService.updateToken(customerId, tokenId, request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setEditTokenDialog({ open: false, customerId: null, token: null });
    },
  });

  const deleteTokenMutation = useMutation({
    mutationFn: ({ customerId, tokenId }: { customerId: number; tokenId: number }) =>
      customerService.deleteToken(customerId, tokenId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setDeleteTokenDialog({ open: false, customerId: null, token: null });
    },
  });

  const handleCopyTokenUrl = (tokenGuid: string) => {
    // Get API base URL and remove the /api suffix to get the root URL
    const apiBaseUrl = env.API_BASE_URL.replace(/\/api\/?$/, '');
    const downloadUrl = `${apiBaseUrl}/blacklist/${tokenGuid}/download`;
    navigator.clipboard.writeText(downloadUrl);
    setCopiedTokenGuid(tokenGuid);
    setTimeout(() => setCopiedTokenGuid(null), 2000);
  };

  if (isLoading) return <Box>Loading...</Box>;

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" gap={2}>
          <PeopleIcon sx={{ fontSize: 32 }} />
          <Typography variant="h4">Customer Management</Typography>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddCustomerDialogOpen(true)}
        >
          Add Customer
        </Button>
      </Stack>

      {/* Download Statistics Section */}
      <Card sx={{ mb: 3, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <DownloadIcon />
            <Typography variant="h6">Download Statistics</Typography>
          </Stack>
          <Stack direction="row" gap={1}>
            <Button
              size="small"
              variant={statsDays === 7 ? 'contained' : 'outlined'}
              onClick={() => setStatsDays(7)}
            >
              7 days
            </Button>
            <Button
              size="small"
              variant={statsDays === 30 ? 'contained' : 'outlined'}
              onClick={() => setStatsDays(30)}
            >
              30 days
            </Button>
            <Button
              size="small"
              variant={statsDays === 90 ? 'contained' : 'outlined'}
              onClick={() => setStatsDays(90)}
            >
              90 days
            </Button>
          </Stack>
        </Stack>

        {downloadStats && (
          <>
            <Stack direction="row" gap={2} sx={{ mb: 3 }}>
              <Card sx={{ flex: 1, p: 2, bgcolor: '#f0f9ff' }}>
                <Typography variant="caption" color="text.secondary">Total Downloads</Typography>
                <Typography variant="h5" fontWeight="bold">{downloadStats.totalDownloads}</Typography>
              </Card>
              <Card sx={{ flex: 1, p: 2, bgcolor: '#f0fdf4' }}>
                <Typography variant="caption" color="text.secondary">Successful</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {downloadStats.successfulDownloads}
                </Typography>
              </Card>
              <Card sx={{ flex: 1, p: 2, bgcolor: '#fef2f2' }}>
                <Typography variant="caption" color="text.secondary">Failed</Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">
                  {downloadStats.failedDownloads}
                </Typography>
              </Card>
              <Card sx={{ flex: 1, p: 2, bgcolor: '#fafaf9' }}>
                <Typography variant="caption" color="text.secondary">Total Data</Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatBytes(downloadStats.totalBytesDownloaded)}
                </Typography>
              </Card>
            </Stack>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Downloads by Customer
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Customer</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Success</TableCell>
                    <TableCell align="right">Failed</TableCell>
                    <TableCell align="right">Data Downloaded</TableCell>
                    <TableCell>Last Download</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {downloadStats.customerBreakdown.map((customer) => (
                    <TableRow key={customer.customerId || 0}>
                      <TableCell>{customer.customerName}</TableCell>
                      <TableCell align="right">{customer.totalDownloads}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={customer.successfulDownloads}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {customer.failedDownloads > 0 ? (
                          <Chip
                            label={customer.failedDownloads}
                            size="small"
                            color="error"
                            variant="outlined"
                          />
                        ) : (
                          '-'
                        )}
                      </TableCell>
                      <TableCell align="right">{formatBytes(customer.totalBytesDownloaded)}</TableCell>
                      <TableCell>
                        {customer.lastDownload
                          ? format(new Date(customer.lastDownload), 'PP p')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Card>

      {/* Customer List */}
      <Typography variant="h6" sx={{ mb: 2 }}>Customers</Typography>
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>Name</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Tokens</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers?.map((customer) => (
                <React.Fragment key={customer.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() =>
                          setExpandedCustomer(expandedCustomer === customer.id ? null : customer.id)
                        }
                      >
                        {expandedCustomer === customer.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{customer.name}</TableCell>
                    <TableCell>{customer.contact}</TableCell>
                    <TableCell>
                      <Chip label={customer.tokenCount} size="small" />
                    </TableCell>
                    <TableCell>{format(new Date(customer.createdAt), 'PP')}</TableCell>
                    <TableCell>
                      <Chip
                        label={customer.isActive ? 'Active' : 'Inactive'}
                        color={customer.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => setEditCustomerDialog({ open: true, customer })}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteCustomerDialog({ open: true, customer })}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={7}>
                      <Collapse in={expandedCustomer === customer.id} timeout="auto" unmountOnExit>
                        <Box sx={{ margin: 2 }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                            <Typography variant="h6">Download Tokens</Typography>
                            <Button
                              size="small"
                              startIcon={<AddIcon />}
                              onClick={() => setAddTokenDialog({ open: true, customerId: customer.id })}
                            >
                              Add Token
                            </Button>
                          </Stack>
                          {expandedCustomer === customer.id && !customerDetails ? (
                            <Box sx={{ textAlign: 'center', py: 2 }}>Loading tokens...</Box>
                          ) : (customerDetails?.tokens || []).length === 0 ? (
                            <Alert severity="info">No tokens yet. Create one to allow downloads.</Alert>
                          ) : (
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell>Name</TableCell>
                                  <TableCell>Description</TableCell>
                                  <TableCell>Download URL</TableCell>
                                  <TableCell>Rate Limit</TableCell>
                                  <TableCell>Usage Today</TableCell>
                                  <TableCell>Expires</TableCell>
                                  <TableCell>Status</TableCell>
                                  <TableCell align="right">Actions</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {(customerDetails?.tokens || []).map((token) => (
                                  <TableRow key={token.id}>
                                    <TableCell>{token.name}</TableCell>
                                    <TableCell>{token.description || '-'}</TableCell>
                                    <TableCell>
                                      <Stack direction="row" alignItems="center" gap={1}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                          ...{token.tokenGuid.substring(0, 8)}
                                        </Typography>
                                        <Tooltip title={copiedTokenGuid === token.tokenGuid ? 'Copied!' : 'Copy URL'}>
                                          <IconButton size="small" onClick={() => handleCopyTokenUrl(token.tokenGuid)}>
                                            <ContentCopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Stack>
                                    </TableCell>
                                    <TableCell>
                                      {token.dailyDownloadLimit ? `${token.dailyDownloadLimit}/day` : 'Unlimited'}
                                    </TableCell>
                                    <TableCell>
                                      {token.downloadsToday !== undefined && token.downloadsToday !== null
                                        ? token.dailyDownloadLimit
                                          ? `${token.downloadsToday}/${token.dailyDownloadLimit}`
                                          : `${token.downloadsToday}`
                                        : '0'}
                                    </TableCell>
                                    <TableCell>
                                      {token.expiresAt ? format(new Date(token.expiresAt), 'PP') : 'Never'}
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={token.isActive ? 'Active' : 'Inactive'}
                                        color={token.isActive ? 'success' : 'default'}
                                        size="small"
                                      />
                                    </TableCell>
                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setEditTokenDialog({
                                            open: true,
                                            customerId: customer.id,
                                            token,
                                          })
                                        }
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setDeleteTokenDialog({
                                            open: true,
                                            customerId: customer.id,
                                            token,
                                          })
                                        }
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Add Customer Dialog */}
      <AddCustomerDialog
        open={addCustomerDialogOpen}
        onClose={() => setAddCustomerDialogOpen(false)}
        onSubmit={(request) => createCustomerMutation.mutate(request)}
        isLoading={createCustomerMutation.isPending}
      />

      {/* Edit Customer Dialog */}
      {editCustomerDialog.customer && (
        <EditCustomerDialog
          open={editCustomerDialog.open}
          customer={editCustomerDialog.customer}
          onClose={() => setEditCustomerDialog({ open: false, customer: null })}
          onSubmit={(request) =>
            updateCustomerMutation.mutate({ id: editCustomerDialog.customer!.id, request })
          }
          isLoading={updateCustomerMutation.isPending}
        />
      )}

      {/* Delete Customer Confirmation */}
      <Dialog
        open={deleteCustomerDialog.open}
        onClose={() => setDeleteCustomerDialog({ open: false, customer: null })}
      >
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete customer <strong>{deleteCustomerDialog.customer?.name}</strong>?
            {deleteCustomerDialog.customer && deleteCustomerDialog.customer.tokenCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                This customer has {deleteCustomerDialog.customer.tokenCount} active token(s). 
                You must delete or deactivate all tokens first.
              </Alert>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCustomerDialog({ open: false, customer: null })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteCustomerMutation.mutate(deleteCustomerDialog.customer!.id)}
            disabled={deleteCustomerMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Token Dialog */}
      {addTokenDialog.customerId && (
        <AddTokenDialog
          open={addTokenDialog.open}
          onClose={() => setAddTokenDialog({ open: false, customerId: null })}
          onSubmit={(request) =>
            createTokenMutation.mutate({ customerId: addTokenDialog.customerId!, request })
          }
          isLoading={createTokenMutation.isPending}
        />
      )}

      {/* Edit Token Dialog */}
      {editTokenDialog.token && (
        <EditTokenDialog
          open={editTokenDialog.open}
          token={editTokenDialog.token}
          onClose={() => setEditTokenDialog({ open: false, customerId: null, token: null })}
          onSubmit={(request) =>
            updateTokenMutation.mutate({
              customerId: editTokenDialog.customerId!,
              tokenId: editTokenDialog.token!.id,
              request,
            })
          }
          isLoading={updateTokenMutation.isPending}
        />
      )}

      {/* Delete Token Confirmation */}
      <Dialog
        open={deleteTokenDialog.open}
        onClose={() => setDeleteTokenDialog({ open: false, customerId: null, token: null })}
      >
        <DialogTitle>Delete Token</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete token <strong>{deleteTokenDialog.token?.name}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTokenDialog({ open: false, customerId: null, token: null })}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() =>
              deleteTokenMutation.mutate({
                customerId: deleteTokenDialog.customerId!,
                tokenId: deleteTokenDialog.token!.id,
              })
            }
            disabled={deleteTokenMutation.isPending}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Helper dialog components
const AddCustomerDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (request: CreateCustomerRequest) => void;
  isLoading: boolean;
}> = ({ open, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');

  const handleSubmit = () => {
    if (!name.trim() || !contact.trim()) return;
    onSubmit({ name, contact });
    setName('');
    setContact('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Customer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Contact (Email/Phone)"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            required
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || !contact.trim() || isLoading}
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditCustomerDialog: React.FC<{
  open: boolean;
  customer: Customer;
  onClose: () => void;
  onSubmit: (request: UpdateCustomerRequest) => void;
  isLoading: boolean;
}> = ({ open, customer, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState(customer.name);
  const [contact, setContact] = useState(customer.contact);
  const [isActive, setIsActive] = useState(customer.isActive);

  const handleSubmit = () => {
    if (!name.trim() || !contact.trim()) return;
    onSubmit({ name, contact, isActive });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Customer</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Contact (Email/Phone)"
            value={contact}
            onChange={(e) => setContact(e.target.value)}
            required
            fullWidth
          />
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography>Status:</Typography>
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'default'}
              onClick={() => setIsActive(!isActive)}
              clickable
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || !contact.trim() || isLoading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AddTokenDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSubmit: (request: CreateDownloadTokenRequest) => void;
  isLoading: boolean;
}> = ({ open, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [dailyDownloadLimit, setDailyDownloadLimit] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({
      name,
      description: description || undefined,
      dailyDownloadLimit: dailyDownloadLimit ? parseInt(dailyDownloadLimit) : undefined,
      expiresAt: expiresAt || undefined,
    });
    setName('');
    setDescription('');
    setDailyDownloadLimit('');
    setExpiresAt('');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add Download Token</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            helperText="e.g., 'Production Server', 'Test Environment'"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Daily Download Limit"
            value={dailyDownloadLimit}
            onChange={(e) => setDailyDownloadLimit(e.target.value)}
            type="number"
            fullWidth
            helperText="Leave empty for unlimited downloads"
            InputProps={{
              endAdornment: <InputAdornment position="end">/day</InputAdornment>,
            }}
          />
          <TextField
            label="Expires At"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            type="datetime-local"
            fullWidth
            helperText="Leave empty for no expiration"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || isLoading}
        >
          Create Token
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const EditTokenDialog: React.FC<{
  open: boolean;
  token: CustomerDownloadToken;
  onClose: () => void;
  onSubmit: (request: UpdateDownloadTokenRequest) => void;
  isLoading: boolean;
}> = ({ open, token, onClose, onSubmit, isLoading }) => {
  const [name, setName] = useState(token.name);
  const [description, setDescription] = useState(token.description || '');
  const [dailyDownloadLimit, setDailyDownloadLimit] = useState<string>(
    token.dailyDownloadLimit?.toString() || ''
  );
  const [expiresAt, setExpiresAt] = useState<string>(
    token.expiresAt ? new Date(token.expiresAt).toISOString().slice(0, 16) : ''
  );
  const [isActive, setIsActive] = useState(token.isActive);

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSubmit({
      name,
      description: description || undefined,
      dailyDownloadLimit: dailyDownloadLimit ? parseInt(dailyDownloadLimit) : undefined,
      expiresAt: expiresAt || undefined,
      isActive,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Download Token</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Daily Download Limit"
            value={dailyDownloadLimit}
            onChange={(e) => setDailyDownloadLimit(e.target.value)}
            type="number"
            fullWidth
            InputProps={{
              endAdornment: <InputAdornment position="end">/day</InputAdornment>,
            }}
          />
          <TextField
            label="Expires At"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography>Status:</Typography>
            <Chip
              label={isActive ? 'Active' : 'Inactive'}
              color={isActive ? 'success' : 'default'}
              onClick={() => setIsActive(!isActive)}
              clickable
            />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!name.trim() || isLoading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};
